import React, { useCallback, useEffect, useMemo, useState } from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarIcon,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  Download,
  FileText,
  MoreHorizontal,
  Search,
  X,
} from "lucide-react";
import * as XLSX from "xlsx";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { StatCardsSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { useAuthCheck } from "~/hooks/useAuthCheck";
import { type Check, useChecks } from "~/hooks/useChecks";
import { supabase } from "~/integrations/supabase/client";
import { handleAndNotify, handleError } from "~/lib/errorHandler";
import EditCheckDatesDialog from "~/pages/payments/components/EditCheckDatesDialog";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { formatCurrency } from "~/utils/helpers";
import { resolveGross } from "~/utils/iva";

const ChequesPage: React.FC = () => {
  const { userRole, isLoading } = useAuthCheck("/cheques");
  const {
    data: checks = [],
    isLoading: loading,
    error: checksError,
    refetch: refetchChecks,
  } = useChecks();
  const [updatingCheckId, setUpdatingCheckId] = useState<number | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [items, setItems] = useState<any[]>([]);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFromFilter, setDateFromFilter] = useState<string>("");
  const [dateToFilter, setDateToFilter] = useState<string>("");
  const [dateFilterType, setDateFilterType] =
    useState<string>("collection_date");

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);

  // Sorting state
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Bulk selection states
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<number>>(
    new Set()
  );
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState<string>("");

  // Edit dates dialog state
  const [editingCheck, setEditingCheck] = useState<Check | null>(null);
  const [isEditDatesDialogOpen, setIsEditDatesDialogOpen] = useState(false);

  // Handle query errors
  useEffect(() => {
    if (checksError) {
      console.error("Error fetching checks:", checksError);
      toast({
        title: "Error",
        description: "No se pudieron cargar los cheques",
        variant: "destructive",
      });
    }
  }, [checksError]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters, search, and sorting
  const filteredAndSortedChecks = useMemo(() => {
    let filtered = [...checks];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (check) => check.check_status === statusFilter
      );
    }

    // Date range filter (based on selected date type)
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter((check) => {
        let dateToCompare: string | null = null;

        if (dateFilterType === "collection_date") {
          dateToCompare = check.collection_date;
        } else if (dateFilterType === "reception_date") {
          dateToCompare = check.reception_date;
        } else if (dateFilterType === "created_at") {
          dateToCompare = check.created_at;
        }

        if (!dateToCompare) return false;

        // Convert dates to simple YYYY-MM-DD format strings for comparison
        const checkDate = new Date(dateToCompare);
        const checkDateString = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, "0")}-${String(checkDate.getDate()).padStart(2, "0")}`;

        // Handle date from filter
        if (dateFromFilter) {
          if (checkDateString < dateFromFilter) return false;
        }

        // Handle date to filter
        if (dateToFilter) {
          if (checkDateString > dateToFilter) return false;
        }

        return true;
      });
    }

    // Search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (check) =>
          check.check_number?.toLowerCase().includes(searchLower) ||
          check.client_name?.toLowerCase().includes(searchLower) ||
          check.bank?.toLowerCase().includes(searchLower) ||
          check.factura_number?.toLowerCase().includes(searchLower) ||
          check.reference?.toLowerCase().includes(searchLower)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "check_number":
          aValue = a.check_number || "";
          bValue = b.check_number || "";
          break;
        case "client_name":
          aValue = a.client_name;
          bValue = b.client_name;
          break;
        case "bank":
          aValue = a.bank || "";
          bValue = b.bank || "";
          break;
        case "amount":
          aValue = a.amount;
          bValue = b.amount;
          break;
        case "reception_date":
          aValue = a.reception_date ? new Date(a.reception_date) : new Date(0);
          bValue = b.reception_date ? new Date(b.reception_date) : new Date(0);
          break;
        case "collection_date":
          aValue = a.collection_date
            ? new Date(a.collection_date)
            : new Date(0);
          bValue = b.collection_date
            ? new Date(b.collection_date)
            : new Date(0);
          break;
        case "check_status":
          aValue = a.check_status;
          bValue = b.check_status;
          break;
        default:
          aValue = a.collection_date
            ? new Date(a.collection_date)
            : new Date(0);
          bValue = b.collection_date
            ? new Date(b.collection_date)
            : new Date(0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    checks,
    statusFilter,
    dateFromFilter,
    dateToFilter,
    dateFilterType,
    debouncedSearchTerm,
    sortField,
    sortDirection,
  ]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredAndSortedChecks]);

  // Get paginated data
  const paginatedChecks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedChecks.slice(startIndex, endIndex);
  }, [filteredAndSortedChecks, currentPage, pageSize]);

  // Calculate pagination info
  const totalPages = Math.ceil(filteredAndSortedChecks.length / pageSize);
  const totalChecks = filteredAndSortedChecks.length;

  // Clear selection when filters change
  useEffect(() => {
    clearSelection();
  }, [
    statusFilter,
    dateFromFilter,
    dateToFilter,
    dateFilterType,
    debouncedSearchTerm,
  ]);

  const clearFilters = () => {
    setStatusFilter("all");
    setDateFromFilter("");
    setDateToFilter("");
    setDateFilterType("collection_date");
    setSearchTerm("");
  };

  // Sorting functions
  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    },
    [sortField, sortDirection]
  );

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="ml-2 h-4 w-4" />
    ) : (
      <ChevronDown className="ml-2 h-4 w-4" />
    );
  };

  // Pagination functions
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Bulk selection functions
  const handleSelectCheck = (checkId: number) => {
    const newSelected = new Set(selectedCheckIds);
    if (newSelected.has(checkId)) {
      newSelected.delete(checkId);
    } else {
      newSelected.add(checkId);
    }
    setSelectedCheckIds(newSelected);
    setIsSelectAllChecked(
      newSelected.size === filteredAndSortedChecks.length &&
        filteredAndSortedChecks.length > 0
    );
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedCheckIds(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allCheckIds = new Set(
        filteredAndSortedChecks.map((check) => check.id)
      );
      setSelectedCheckIds(allCheckIds);
      setIsSelectAllChecked(true);
    }
  };

  const clearSelection = () => {
    setSelectedCheckIds(new Set());
    setIsSelectAllChecked(false);
    setSelectedBulkStatus("");
  };

  // Confirm bulk status update function
  const confirmBulkStatusUpdate = async () => {
    if (!selectedBulkStatus || selectedCheckIds.size === 0) return;
    await handleBulkStatusUpdate(selectedBulkStatus);
  };

  // Bulk status update function
  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedCheckIds.size === 0) return;

    const selectedChecks = checks.filter((check) =>
      selectedCheckIds.has(check.id)
    );
    const checkCount = selectedChecks.length;

    // Show confirmation dialog for protested status
    if (newStatus === "protested") {
      const confirmResult = confirm(
        `¿Está seguro que desea marcar ${checkCount} cheque${checkCount > 1 ? "s" : ""} como protestado${checkCount > 1 ? "s" : ""}?`
      );
      if (!confirmResult) return;
    }

    setIsBulkUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process each selected check
      for (const check of selectedChecks) {
        try {
          // Update both check_status and payment_status when check is collected or paid
          const updateData: any = { check_status: newStatus };
          if (newStatus === "collected" || newStatus === "paid") {
            updateData.payment_status = "completed";
            // Auto-verify check payments when marked as paid
            if (newStatus === "paid") {
              updateData.is_verified = true;
              updateData.verified_at = new Date().toISOString();
              // Note: verified_by would need to be set by auth context if user ID is available
            }
          }

          const { error } = await supabase
            .from("payments")
            .update(updateData)
            .eq("id", check.id);

          if (error) throw error;

          // Handle sales request update when check status changes to collected or paid
          if (
            (newStatus === "collected" || newStatus === "paid") &&
            check.sales_request_id &&
            check.check_status !== newStatus
          ) {
            try {
              // Recalculate paid amount using RPC function that includes collected/paid checks
              const { data: newPaidAmount, error: paidAmountError } =
                await supabase.rpc("get_sales_request_paid_amount", {
                  sales_request_id: check.sales_request_id,
                });

              if (paidAmountError) {
                console.error(
                  "Error calculating paid amount:",
                  paidAmountError
                );
              } else {
                // Get the sales request to check total amount for paid calculation
                const { data: salesRequestData, error: fetchError } =
                  await supabase
                    .from("sales_request")
                    .select("price_with_iva, total_amount, is_exento")
                    .eq("id", check.sales_request_id)
                    .single();

                if (fetchError) {
                  console.error("Error fetching sales request:", fetchError);
                } else {
                  const totalAmount = resolveGross(salesRequestData);
                  const isPaid = (Number(newPaidAmount) || 0) >= totalAmount;

                  // Update the sales request with new paid amount and status
                  const { error: updateError } = await supabase
                    .from("sales_request")
                    .update({
                      paid_amount: Number(newPaidAmount) || 0,
                      paid: isPaid,
                    })
                    .eq("id", check.sales_request_id);

                  if (updateError) {
                    console.error("Error updating sales request:", updateError);
                  }
                }
              }
            } catch (updateError) {
              console.error(
                "Error updating sales request paid amount:",
                updateError
              );
            }
          }

          successCount++;
        } catch (error) {
          handleError(error, {
            context: "bulkUpdateCheck",
            component: "ChequesPage",
            metadata: { checkId: check.id, newStatus },
          });
          errorCount++;
        }
      }

      // Refetch data to get updated state
      refetchChecks();

      // Clear selection
      clearSelection();

      // Show success/error message
      if (errorCount === 0) {
        toast({
          title: "Estado actualizado",
          description: `${successCount} cheque${successCount > 1 ? "s" : ""} actualizado${successCount > 1 ? "s" : ""} correctamente`,
        });
      } else {
        toast({
          title: "Actualización parcial",
          description: `${successCount} cheque${successCount > 1 ? "s" : ""} actualizado${successCount > 1 ? "s" : ""}, ${errorCount} error${errorCount > 1 ? "es" : ""}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "No se pudieron actualizar los cheques",
        context: "handleBulkStatusUpdate",
        component: "ChequesPage",
        metadata: { selectedCount: selectedChecks.length, newStatus },
      });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleOpenOrderDialog = async (salesRequestId: string) => {
    try {
      // Fetch the sales request data
      const { data: orderData, error: orderError } = await supabase
        .from("sales_request")
        .select("*")
        .eq("id", salesRequestId)
        .single();

      if (orderError) throw orderError;

      // Fetch items for this sales request
      const { data: itemsData, error: itemsError } = await supabase
        .from("sales_request_items")
        .select("*")
        .eq("sales_request_id", salesRequestId);

      if (itemsError) throw itemsError;

      // Fetch client name
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .select("name")
        .eq("id", orderData.client_id)
        .single();

      if (clientError) throw clientError;

      setSelectedOrder(orderData);
      setItems(itemsData || []);
      setClientNames({ [orderData.client_id]: clientData.name });
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "No se pudo cargar la información de la orden",
        context: "handleOpenOrderDialog",
        component: "ChequesPage",
        metadata: { salesRequestId },
      });
    }
  };

  const getSellerInfo = (sellerId: string) => ({
    name: "Vendedor",
    lastname: "",
    email: "",
  });

  const updateCheckStatus = async (checkId: number, newStatus: string) => {
    try {
      setUpdatingCheckId(checkId);

      // Get the current check data for credit operations
      const currentCheck = checks.find((check) => check.id === checkId);
      if (!currentCheck) {
        throw new Error("Check not found");
      }

      // Update both check_status and payment_status when check is collected or paid
      const updateData: any = { check_status: newStatus };
      if (newStatus === "collected" || newStatus === "paid") {
        updateData.payment_status = "completed";
        // Auto-verify check payments when marked as paid
        if (newStatus === "paid") {
          updateData.is_verified = true;
          updateData.verified_at = new Date().toISOString();
          // Note: verified_by would need to be set by auth context if user ID is available
        }
      }

      const { error } = await supabase
        .from("payments")
        .update(updateData)
        .eq("id", checkId);

      if (error) throw error;

      // Refetch data to get updated state
      refetchChecks();

      // Handle sales request update when check status changes to collected or paid
      if (
        (newStatus === "collected" || newStatus === "paid") &&
        currentCheck.sales_request_id &&
        currentCheck.check_status !== newStatus
      ) {
        try {
          // Recalculate paid amount using RPC function that includes collected/paid checks
          const { data: newPaidAmount, error: paidAmountError } =
            await supabase.rpc("get_sales_request_paid_amount", {
              sales_request_id: currentCheck.sales_request_id,
            });

          if (paidAmountError) {
            console.error("Error calculating paid amount:", paidAmountError);
          } else {
            // Get the sales request to check total amount for paid calculation
            const { data: salesRequestData, error: fetchError } = await supabase
              .from("sales_request")
              .select("price_with_iva, total_amount, is_exento")
              .eq("id", currentCheck.sales_request_id)
              .single();

            if (fetchError) {
              console.error("Error fetching sales request:", fetchError);
            } else {
              const totalAmount = resolveGross(salesRequestData);
              const isPaid = (Number(newPaidAmount) || 0) >= totalAmount;

              // Update the sales request with new paid amount and status
              const { error: updateError } = await supabase
                .from("sales_request")
                .update({
                  paid_amount: Number(newPaidAmount) || 0,
                  paid: isPaid,
                })
                .eq("id", currentCheck.sales_request_id);

              if (updateError) {
                console.error("Error updating sales request:", updateError);
              } else {
                console.log(
                  `Sales request ${currentCheck.sales_request_id} updated with paid amount: ${newPaidAmount}, paid status: ${isPaid}`
                );
              }
            }
          }
        } catch (updateError) {
          console.error(
            "Error updating sales request paid amount:",
            updateError
          );
        }
      }

      // Handle credit release when check is collected
      if (
        newStatus === "collected" &&
        currentCheck.check_status !== "collected"
      ) {
        // Credit is automatically released because creditUtilizationUtils.ts
        // now excludes collected checks from credit calculations
        console.log(
          `Credit released for check ${currentCheck.check_number} - amount: ${currentCheck.amount}`
        );
      }

      // Send notification if marking as protested
      if (newStatus === "protested") {
        try {
          const { data, error: edgeError } = await supabase.functions.invoke(
            "notify-protested-check",
            {
              body: { payment_id: checkId },
            }
          );

          if (edgeError) {
            console.error("Edge function error:", edgeError);
            toast({
              title: "Estado actualizado",
              description:
                "Cheque marcado como protestado, pero no se pudo enviar la notificación",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Cheque marcado como protestado",
              description:
                "Se ha enviado notificación al cliente y al equipo de cobranza",
            });
          }
        } catch (notificationError) {
          console.error("Error sending notification:", notificationError);
          toast({
            title: "Estado actualizado",
            description:
              "Cheque marcado como protestado, pero no se pudo enviar la notificación",
            variant: "destructive",
          });
        }
      } else {
        // Show appropriate success message based on status
        let successMessage = `El cheque ha sido marcado como ${getStatusLabel(newStatus)}`;

        if (newStatus === "collected") {
          successMessage = `Cheque #${currentCheck.check_number} cobrado exitosamente. Crédito liberado automáticamente.`;
        } else if (newStatus === "in_portfolio") {
          successMessage = `Cheque #${currentCheck.check_number} colocado en cartera. Crédito restringido hasta el cobro.`;
        }

        toast({
          title: "Estado actualizado",
          description: successMessage,
        });
      }
    } catch (error: any) {
      handleAndNotify(error, {
        userMessage: "No se pudo actualizar el estado del cheque",
        context: "updateCheckStatus",
        component: "ChequesPage",
        metadata: {
          checkId,
          newStatus,
          checkNumber: currentCheck?.check_number,
        },
      });
    } finally {
      setUpdatingCheckId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Pendiente";
      case "in_portfolio":
        return "En Cartera";
      case "collected":
        return "Cobrado";
      case "paid":
        return "Pagado";
      case "protested":
        return "Protestado";
      default:
        return status;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-800">
            <Clock className="mr-1 h-3 w-3" />
            Pendiente
          </Badge>
        );
      case "in_portfolio":
        return (
          <Badge variant="secondary" className="bg-orange-100 text-orange-800">
            <FileText className="mr-1 h-3 w-3" />
            En Cartera
          </Badge>
        );
      case "collected":
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Cobrado
          </Badge>
        );
      case "paid":
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Pagado
          </Badge>
        );
      case "protested":
        return (
          <Badge variant="secondary" className="bg-red-100 text-red-800">
            <X className="mr-1 h-3 w-3" />
            Protestado
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const exportToExcel = () => {
    // Group checks by check_number to consolidate duplicates
    const consolidatedChecks = new Map();

    filteredAndSortedChecks.forEach((check) => {
      const key = check.check_number
        ? `${check.client_id}_${check.check_number}`
        : `unknown_${check.id}`;

      if (consolidatedChecks.has(key)) {
        // If check already exists, append the factura to existing facturas and sum amounts
        const existing = consolidatedChecks.get(key);
        const currentFactura = getFacturaDisplay(check);
        if (
          currentFactura !== "-" &&
          !existing.facturas.includes(currentFactura)
        ) {
          existing.facturas.push(currentFactura);
        }
        // Sum the amounts
        existing.amount += check.amount;
      } else {
        // Create new consolidated check entry
        const currentFactura = getFacturaDisplay(check);
        consolidatedChecks.set(key, {
          check_number: check.check_number || "N/A",
          client_name: check.client_name,
          amount: check.amount,
          bank: check.bank || "N/A",
          facturas: currentFactura !== "-" ? [currentFactura] : [],
          reception_date: check.reception_date
            ? format(new Date(check.reception_date), "dd/MM/yyyy", {
                locale: es,
              })
            : "N/A",
          collection_date: check.collection_date
            ? format(new Date(check.collection_date), "dd/MM/yyyy", {
                locale: es,
              })
            : "N/A",
          status: getStatusLabel(check.check_status),
          reference: check.reference,
          created_at: format(new Date(check.created_at), "dd/MM/yyyy", {
            locale: es,
          }),
        });
      }
    });

    // Convert consolidated checks to export format with Spanish headers
    const exportData = Array.from(consolidatedChecks.values()).map((check) => ({
      "Número de Cheque": check.check_number,
      Cliente: check.client_name,
      Monto: check.amount,
      Banco: check.bank,
      "Factura/Nota":
        check.facturas.length > 0 ? check.facturas.join(", ") : "-",
      "Fecha de Recepción": check.reception_date,
      "Fecha de Cobro": check.collection_date,
      Estado: check.status,
      Referencia: check.reference,
      "Fecha de Creación": check.created_at,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths for better readability
    ws["!cols"] = [
      { width: 20 }, // Número de Cheque
      { width: 30 }, // Cliente
      { width: 15 }, // Monto
      { width: 20 }, // Banco
      { width: 25 }, // Factura/Nota
      { width: 18 }, // Fecha de Recepción
      { width: 18 }, // Fecha de Cobro
      { width: 15 }, // Estado
      { width: 20 }, // Referencia
      { width: 18 }, // Fecha de Creación
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cheques");
    XLSX.writeFile(wb, "cheques.xlsx");

    toast({
      title: "Exportación exitosa",
      description: "Los datos de cheques han sido exportados a Excel",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "text-yellow-600";
      case "collected":
        return "text-green-600";
      case "paid":
        return "text-blue-600";
      case "protested":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  // Helper function to format factura display
  const getFacturaDisplay = (check: Check) => {
    if (check.factura_number) {
      return check.factura_number;
    }
    // Show first 8 characters of sales_request_id if no factura
    if (check.sales_request_id) {
      const srId = String(check.sales_request_id);
      return srId.substring(0, 8) + "...";
    }
    return "-";
  };

  useEffect(() => {
    if (!isLoading && userRole) {
      refetchChecks();
    }
  }, [userRole, isLoading]);

  if (isLoading || !userRole) {
    return (
      <div className="flex h-screen items-center justify-center">
        Cargando...
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Gestión de Cheques"
        description="Gestiona el estado y seguimiento de los cheques recibidos."
        actions={
          <>
            <Button
              onClick={() => refetchChecks()}
              variant="outline"
              className="h-9"
              disabled={loading}
            >
              {loading ? "Cargando..." : "Actualizar"}
            </Button>
            <Button
              onClick={exportToExcel}
              variant="outline"
              className="h-9"
              disabled={loading || filteredAndSortedChecks.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar Excel
            </Button>
          </>
        }
      />

      {/* Summary stats */}
      {loading ? (
        <StatCardsSkeleton count={5} className="lg:grid-cols-5" />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Cheques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {filteredAndSortedChecks.length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                En Cartera
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {
                  filteredAndSortedChecks.filter(
                    (c) => c.check_status === "in_portfolio"
                  ).length
                }
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(
                  filteredAndSortedChecks
                    .filter((c) => c.check_status === "in_portfolio")
                    .reduce((sum, c) => sum + c.amount, 0)
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pendientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {
                  filteredAndSortedChecks.filter(
                    (c) => c.check_status === "pending"
                  ).length
                }
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(
                  filteredAndSortedChecks
                    .filter((c) => c.check_status === "pending")
                    .reduce((sum, c) => sum + c.amount, 0)
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Cobrados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {
                  filteredAndSortedChecks.filter(
                    (c) => c.check_status === "collected"
                  ).length
                }
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(
                  filteredAndSortedChecks
                    .filter((c) => c.check_status === "collected")
                    .reduce((sum, c) => sum + c.amount, 0)
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Protestados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {
                  filteredAndSortedChecks.filter(
                    (c) => c.check_status === "protested"
                  ).length
                }
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(
                  filteredAndSortedChecks
                    .filter((c) => c.check_status === "protested")
                    .reduce((sum, c) => sum + c.amount, 0)
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span className="font-medium">Buscar y Filtrar</span>
          </div>

          {/* Search Input */}
          <div className="mb-3">
            <Label htmlFor="search">Buscar cheques</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Buscar por número, cliente, banco, factura o referencia..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 w-full pl-8"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label htmlFor="status-filter">Estado</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 h-9 w-full">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="in_portfolio">En Cartera</SelectItem>
                  <SelectItem value="collected">Cobrado</SelectItem>
                  <SelectItem value="paid">Pagado</SelectItem>
                  <SelectItem value="protested">Protestado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date-filter-type">Filtrar por fecha</Label>
              <Select value={dateFilterType} onValueChange={setDateFilterType}>
                <SelectTrigger className="mt-1 h-9 w-full">
                  <SelectValue placeholder="Tipo de fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="collection_date">
                    Fecha de cobro
                  </SelectItem>
                  <SelectItem value="reception_date">
                    Fecha de recepción
                  </SelectItem>
                  <SelectItem value="created_at">Fecha de creación</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date-from">Fecha desde</Label>
              <Input
                id="date-from"
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="mt-1 h-9 w-full"
              />
            </div>
            <div>
              <Label htmlFor="date-to">Fecha hasta</Label>
              <Input
                id="date-to"
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="mt-1 h-9 w-full"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="mt-1 h-9 w-full"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Toolbar */}
      {selectedCheckIds.size > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedCheckIds.size} cheque
              {selectedCheckIds.size > 1 ? "s" : ""} seleccionado
              {selectedCheckIds.size > 1 ? "s" : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              className="h-9 border-blue-200 text-blue-700 hover:bg-blue-100"
            >
              Limpiar selección
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={selectedBulkStatus}
              onValueChange={setSelectedBulkStatus}
              disabled={isBulkUpdating}
            >
              <SelectTrigger className="h-9 w-full sm:w-[200px]">
                <SelectValue
                  placeholder={
                    isBulkUpdating ? "Actualizando..." : "Cambiar estado..."
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Marcar como Pendiente</SelectItem>
                <SelectItem value="in_portfolio">
                  Marcar como En Cartera
                </SelectItem>
                <SelectItem value="collected">Marcar como Cobrado</SelectItem>
                <SelectItem value="paid">Marcar como Pagado</SelectItem>
                <SelectItem value="protested">
                  Marcar como Protestado
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={confirmBulkStatusUpdate}
              disabled={!selectedBulkStatus || isBulkUpdating}
              className="h-9 bg-blue-600 text-white hover:bg-blue-700"
              size="sm"
            >
              {isBulkUpdating ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Actualizando...
                </>
              ) : (
                "Confirmar"
              )}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} columns={10} />
      ) : filteredAndSortedChecks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {checks.length === 0
                ? "No hay cheques registrados"
                : "No hay cheques que coincidan con los filtros"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Mostrando {(currentPage - 1) * pageSize + 1} -{" "}
            {Math.min(currentPage * pageSize, totalChecks)} de {totalChecks}{" "}
            cheques · Página {currentPage} de {totalPages}
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isSelectAllChecked}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all checks"
                      />
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("check_number")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        N° Cheque
                        {getSortIcon("check_number")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("client_name")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Cliente
                        {getSortIcon("client_name")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("bank")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Banco
                        {getSortIcon("bank")}
                      </Button>
                    </TableHead>
                    <TableHead>Factura/Nota</TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("amount")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Monto
                        {getSortIcon("amount")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("reception_date")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Fecha Recepción
                        {getSortIcon("reception_date")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("collection_date")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Fecha para Cobrar
                        {getSortIcon("collection_date")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("check_status")}
                        className="h-8 p-0 font-medium hover:bg-transparent"
                      >
                        Estado
                        {getSortIcon("check_status")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedChecks.map((check, index) => (
                    <TableRow
                      key={check.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedCheckIds.has(check.id)}
                          onCheckedChange={() => handleSelectCheck(check.id)}
                          aria-label={`Select check ${check.check_number}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {check.check_number || "-"}
                      </TableCell>
                      <TableCell>{check.client_name}</TableCell>
                      <TableCell>{check.bank || "-"}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              check.factura_number
                                ? "text-blue-600"
                                : "text-xs text-gray-500"
                            }
                          >
                            {getFacturaDisplay(check)}
                          </span>
                          {check.sales_request_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                              onClick={() =>
                                handleOpenOrderDialog(check.sales_request_id!)
                              }
                              title="Ver detalles de la orden"
                            >
                              <FileText className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(check.amount)}
                      </TableCell>
                      <TableCell>
                        {check.reception_date
                          ? format(
                              new Date(check.reception_date),
                              "dd/MM/yyyy",
                              { locale: es }
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {check.collection_date ? (
                          <span
                            className={`${
                              new Date(check.collection_date) < new Date() &&
                              check.check_status === "pending"
                                ? "font-medium text-red-600"
                                : ""
                            }`}
                          >
                            {format(
                              new Date(check.collection_date),
                              "dd/MM/yyyy",
                              { locale: es }
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(check.check_status)}
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={updatingCheckId === check.id}
                            >
                              <span className="sr-only">Abrir menú</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(check.id, "pending")
                              }
                              className="flex items-center gap-2"
                            >
                              <Clock className="h-4 w-4 text-gray-600" />
                              Marcar como Pendiente
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(check.id, "in_portfolio")
                              }
                              className="flex items-center gap-2"
                            >
                              <FileText className="h-4 w-4 text-orange-600" />
                              Marcar como En Cartera
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(check.id, "collected")
                              }
                              className="flex items-center gap-2"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              Marcar como Cobrado
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(check.id, "paid")
                              }
                              className="flex items-center gap-2"
                            >
                              <CheckCircle className="h-4 w-4 text-blue-600" />
                              Marcar como Pagado
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                updateCheckStatus(check.id, "protested")
                              }
                              className="flex items-center gap-2"
                            >
                              <X className="h-4 w-4 text-red-600" />
                              Marcar como Protestado
                            </DropdownMenuItem>
                            {(userRole === "superadmin" ||
                              userRole === "admin" ||
                              userRole === "cobranza") && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingCheck(check);
                                  setIsEditDatesDialogOpen(true);
                                }}
                                className="flex items-center gap-2"
                              >
                                <CalendarIcon className="h-4 w-4 text-purple-600" />
                                Editar Fechas
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {(dateFromFilter || dateToFilter) && (
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} className="font-semibold">
                        Total ({filteredAndSortedChecks.length} cheques)
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(
                          filteredAndSortedChecks.reduce(
                            (sum, c) => sum + c.amount,
                            0
                          )
                        )}
                      </TableCell>
                      <TableCell colSpan={4} />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </CardContent>
          </Card>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>Ir a página:</span>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className="h-9 w-9 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog
          selectedOrder={selectedOrder}
          setSelectedOrder={setSelectedOrder}
          getSellerInfo={getSellerInfo}
          clientNames={clientNames}
          items={items}
        />
      )}

      {/* Edit Check Dates Dialog */}
      <EditCheckDatesDialog
        open={isEditDatesDialogOpen}
        onOpenChange={setIsEditDatesDialogOpen}
        check={editingCheck}
        onSuccess={() => refetchChecks()}
      />
    </PageContainer>
  );
};

export default ChequesPage;
